import { formatDate } from '@frontend/mwm-ui'
import classNames from 'classnames'
import { MdContentCopy, MdOpenInNew, MdOutlineStar } from 'react-icons/md'

import type { ICellRendererParams } from 'ag-grid-community'
import type { ReactElement } from 'react'
import type { TCategoryId, TStatus } from '~/types/cluster'
import type { MobileDeviceType } from '~/types/device-type'

import AppLogo from '~/components/AppLogo'
import Badge from '~/components/Badge'
import Tooltip from '~/components/Tooltip'
import { APPLICATION_HARDCODED_TAGS, getBadgeLabel } from '~/const/clusters'
import { getStoreLabelFromDeviceType } from '~/utils/application'
import { getFormStatusEmoji } from '~/utils/clusters'
import { copyText } from '~/utils/navigator'
import { formatDecimalNumber } from '~/utils/number'

import './styles.scss'

const MAX_TAGS_ITEM = 3

const forms: { id: TCategoryId; label: string }[] = [
  { id: 'monetization', label: 'Monet' },
  { id: 'product', label: 'Product' },
  { id: 'app_info', label: 'App' },
  { id: 'company_info', label: 'Company' },
]

const getFormStatus = (status: TStatus | null | undefined, form: TCategoryId) => {
  const formLabel = forms.find(({ id }) => id === form)?.label
  return `${getFormStatusEmoji(status)} ${formLabel || ''}`
}

export interface ClusterAppInfos {
  avg_ratings: number | null
  category: string | null
  company_email: string | null
  company_phone_number: string | null
  device_type: MobileDeviceType
  icon_url: string | null
  last_version_date: string | null
  name: string | null
  product_id_data_ai: number | null
  publisher_name: string | null
  status_app_info?: TStatus | null
  status_company_info?: TStatus | null
  status_monetization?: TStatus | null
  status_product?: TStatus | null
  store_id: string
  store_url: string | null
  tags?: string[] | null
}

export interface ClusterApplicationCellContentProps {
  app: ClusterAppInfos
  className?: string
  onContactIconClick?: (email: string, phone: string) => void
  onDeleteTag?: (value: { deviceType: string; storeId: string; tags: string[] }) => void
  withTagsTooltip?: boolean
}

export const ClusterApplicationCellContent = ({
  app,
  className,
  onContactIconClick,
  onDeleteTag,
  withTagsTooltip = false,
}: ClusterApplicationCellContentProps): ReactElement => {
  /** Handlers */

  const handleContactIconClick = () =>
    onContactIconClick && onContactIconClick(app.company_email || '', app.company_phone_number || '')

  const handleCopyDataAiProductId = (value: number) => () => copyText(`${value}`)

  const handleTagDeleteButtonClick = (value: string | number) => {
    onDeleteTag &&
      onDeleteTag({
        deviceType: app.device_type,
        storeId: app.store_id,
        tags: app.tags?.filter((el) => el !== value) || [],
      })
  }

  /** Render */

  return (
    <div className={classNames('cluster-application-cell-renderer__content', className)}>
      <div className="app-logo-and-main-info">
        <AppLogo appLogoUrl={app.icon_url} appPlatform={app.device_type} size="small" />
        <div className="app-logo-and-main-info__main-info">
          <div className="app-name" title={app.name || ''}>
            {app.name || 'Nameless app 🥷'}
          </div>
          {app.category ? <span className="app-category">{app.category}</span> : null}
          {app.avg_ratings || app.product_id_data_ai || app.store_url ? (
            <div className="average-rating-and-company-data-ai-product-id">
              {app.avg_ratings ? (
                <span className="average-rating">
                  <MdOutlineStar color="#ffaa00" size={12} />
                  <span>{formatDecimalNumber(app.avg_ratings)}</span>
                </span>
              ) : null}
              {app.product_id_data_ai ? (
                <span
                  className="company-data-ai-product-id"
                  onClick={handleCopyDataAiProductId(app.product_id_data_ai)}
                >
                  {app.product_id_data_ai}
                  <MdContentCopy className="company-data-ai-product-id__copy-icon" size={11} />
                </span>
              ) : null}
              {app.store_url ? (
                <a className="store-url" href={app.store_url} rel="noreferrer" target="_blank">
                  {getStoreLabelFromDeviceType(app.device_type)}
                  <MdOpenInNew name="external-link" size={11} />
                </a>
              ) : null}
              {onContactIconClick &&
              ((app.company_email && app.company_email !== 'unknown') ||
                (app.company_phone_number && app.company_phone_number !== 'unknown')) ? (
                <>
                  {app.company_email && app.company_email !== 'unknown' ? (
                    <span className="company-email" onClick={handleContactIconClick}>
                      📧
                    </span>
                  ) : null}
                  {app.company_phone_number && app.company_phone_number !== 'unknown' ? (
                    <span className="company-phone" onClick={handleContactIconClick}>
                      📞
                    </span>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {app.publisher_name || app.last_version_date ? (
        <div className="company-name-and-update-date">
          {app.publisher_name ? <span>{app.publisher_name}</span> : null}
          {app.publisher_name && app.last_version_date ? '•' : null}
          {app.last_version_date ? (
            <span>
              Update: <span className="update-date">{formatDate(app.last_version_date, 'DD/MM/YYYY')}</span>
            </span>
          ) : null}
        </div>
      ) : null}
      {app.tags && app.tags.length > 0 ? (
        <div className="app-tags">
          {withTagsTooltip ? (
            <>
              {app.tags.slice(0, MAX_TAGS_ITEM).map((el, idx) => (
                <Badge
                  className={classNames('app-tags__badge', {
                    'app-tags__badge--special': APPLICATION_HARDCODED_TAGS.find(({ value }) => value === el),
                  })}
                  key={idx}
                  label={getBadgeLabel(el, APPLICATION_HARDCODED_TAGS)}
                  onDelete={handleTagDeleteButtonClick}
                  value={el}
                />
              ))}
              {app.tags.length > MAX_TAGS_ITEM ? (
                <Tooltip
                  align={{ points: ['br', 'tr'] }}
                  className="cluster-application-cell-renderer__tags-tooltip"
                  overlay={
                    <>
                      {app.tags.slice(MAX_TAGS_ITEM).map((el, idx) => (
                        <Badge
                          className={classNames('app-tags__badge', {
                            'app-tags__badge--special': APPLICATION_HARDCODED_TAGS.find(({ value }) => value === el),
                          })}
                          key={idx}
                          label={getBadgeLabel(el, APPLICATION_HARDCODED_TAGS)}
                          onDelete={handleTagDeleteButtonClick}
                          value={el}
                        />
                      ))}
                    </>
                  }
                  placement="right"
                  trigger="hover"
                >
                  <div>
                    <Badge className="app-tags__badge" value={`+${app.tags.length - MAX_TAGS_ITEM}`} />
                  </div>
                </Tooltip>
              ) : null}
            </>
          ) : (
            app.tags.map((el, idx) => (
              <Badge className="app-tags__badge" key={idx} onDelete={handleTagDeleteButtonClick} value={el} />
            ))
          )}
        </div>
      ) : null}
      {app.status_monetization || app.status_product || app.status_app_info || app.status_company_info ? (
        <div className="statuses">
          <span
            className={classNames('statuses__item', { 'statuses__item--done': app.status_monetization === 'done' })}
            title={app.status_monetization || ''}
          >
            {getFormStatus(app.status_monetization || null, 'monetization')}
          </span>
          <span
            className={classNames('statuses__item', { 'statuses__item--done': app.status_product === 'done' })}
            title={app.status_product || ''}
          >
            {getFormStatus(app.status_product || null, 'product')}
          </span>
          <span
            className={classNames('statuses__item', { ' statuses__item--done': app.status_app_info === 'done' })}
            title={app.status_app_info || ''}
          >
            {getFormStatus(app.status_app_info || null, 'app_info')}
          </span>
          <span
            className={classNames('statuses__item', { 'statuses__item--done': app.status_company_info === 'done' })}
            title={app.status_company_info || ''}
          >
            {getFormStatus(app.status_company_info || null, 'company_info')}
          </span>
        </div>
      ) : null}
    </div>
  )
}

export interface IContext {
  onContactIconClick?: (email: string, phone: string) => void
  onDeleteTag?: (value: { deviceType: string; storeId: string; tags: string[] }) => void
}

const ClusterApplicationCellRenderer = ({
  context,
  value,
}: ICellRendererParams<unknown, ClusterAppInfos, IContext> &
  Omit<ClusterApplicationCellContentProps, 'app'>): ReactElement => {
  /** Local state */
  if (!value) return <div />

  const { onContactIconClick, onDeleteTag } = context

  /** Render */
  return (
    <div className="cluster-application-cell-renderer">
      <ClusterApplicationCellContent
        app={value}
        onContactIconClick={onContactIconClick}
        onDeleteTag={onDeleteTag}
        withTagsTooltip
      />
    </div>
  )
}

export default ClusterApplicationCellRenderer
